import {
  Box,
  Button,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  styled,
} from '@mui/material';
import React, { useState } from 'react';
import { Link, useRouteMatch } from 'react-router-dom';

import IconButton from '@/@mantis/components/@extended/IconButton';
import { Menu01Icon } from '@/assets/icons/Menu01Icon';
import { Menu02Icon } from '@/assets/icons/Menu02Icon';
import { PMPRoutes, PublicRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';

const NavWrapper = styled(Box)(({ theme }) => ({
  padding: '12px 0 0',
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1000,
}));

const NavContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 12px 12px 16px',
  backgroundColor: 'rgba(255, 255, 255, 0.59)',
  backdropFilter: 'blur(4px)',
  borderRadius: '9px',
  border: '1px solid rgba(0, 0, 0, 0.2)',
  width: 'auto',
  maxWidth: '1336px',
  height: 'auto',
  minHeight: '64px',
  [theme.breakpoints.up('lg')]: {
    width: '1336px',
  },
  [theme.breakpoints.down('lg')]: {
    width: '100%',
  },
}));

const LogoLink = styled(Link)({
  display: 'flex',
  alignItems: 'center',
});

const NavButton = styled(Button)(({ theme }) => ({
  color: '#414651',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '0%',
  textTransform: 'none',
  padding: '10px 6px',
  borderRadius: '8px',
  width: 'auto',
  minWidth: 'auto',
  gap: '4px',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    color: '#212B36',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px',
    padding: '8px 4px',
  },
}));

const NavButtonGroup = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px',
}));

const ActionButtonGroup = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px',
}));

const NavActionContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '16px',
}));

const LoginButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  padding: '8px 22px',
  textTransform: 'none',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '15px',
  lineHeight: '1.5',
  color: '#212B36',
  backgroundColor: '#FFFFFF',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
  border: 'none',
  '&:hover': {
    backgroundColor: '#F4F6F8',
  },
}));

const GetStartedButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  padding: '8px 22px',
  textTransform: 'none',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '15px',
  lineHeight: '1.5',
  color: '#FFFFFF',
  backgroundColor: '#C1170B',
  boxShadow: '0 2px 4px rgba(193, 23, 11, 0.24)',
  border: 'none',
  '&:hover': {
    backgroundColor: '#9E1309',
  },
}));

const NavSection: React.FC = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isAboutUsPage = useRouteMatch(PublicRoutes.aboutUs);
  const isPricingPage = useRouteMatch(PublicRoutes.pricing);
  const { user } = useAuth();

  return (
    <NavWrapper>
      <Container
        maxWidth={false}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <NavContainer>
          <LogoLink to={PublicRoutes.home}>
            <img src="/images/pricing/terrai-logo.svg" alt="Terrai" style={{ height: 32 }} />
          </LogoLink>

          <NavActionContainer sx={{ display: { xs: 'none', md: 'flex' } }}>
            <NavButtonGroup>
              {!isAboutUsPage && <NavButton href={PublicRoutes.aboutUs}>About Us</NavButton>}
              {!isPricingPage && <NavButton href={PublicRoutes.pricing}>Pricing</NavButton>}
            </NavButtonGroup>

            <ActionButtonGroup>
              {user ? (
                <GetStartedButton variant="contained" disableElevation href={PMPRoutes.dashboard}>
                  Dashboard
                </GetStartedButton>
              ) : (
                <>
                  <LoginButton variant="contained" disableElevation href={PublicRoutes.login}>
                    Login
                  </LoginButton>
                  <GetStartedButton variant="contained" disableElevation href={PublicRoutes.signup}>
                    Get Started
                  </GetStartedButton>
                </>
              )}
            </ActionButtonGroup>
          </NavActionContainer>

          <IconButton
            color="primary"
            size="large"
            sx={{
              display: { xs: 'flex', md: 'none' },
              color: '#637381',
              '&:hover': {
                bgcolor: 'transparent',
                opacity: 0.8,
              },
            }}
            onClick={() => setIsDrawerOpen(flag => !flag)}
          >
            {isDrawerOpen ? <Menu02Icon /> : <Menu01Icon />}
          </IconButton>
        </NavContainer>
      </Container>

      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        sx={{
          display: { md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 250,
            backgroundColor: '#FFFFFF',
          },
        }}
      >
        <Box role="presentation" onClick={() => setIsDrawerOpen(false)}>
          <List sx={{ pt: 2 }}>
            <ListItem disablePadding>
              <ListItemButton
                href={PublicRoutes.signup}
                sx={{
                  color: '#414651',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  padding: '10px 16px',
                }}
              >
                <ListItemText
                  primary="Get Started"
                  primaryTypographyProps={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '14px',
                    lineHeight: '20px',
                  }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                href={PublicRoutes.aboutUs}
                sx={{
                  color: '#414651',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  padding: '10px 16px',
                }}
              >
                <ListItemText
                  primary="About Us"
                  primaryTypographyProps={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '14px',
                    lineHeight: '20px',
                  }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                href={PublicRoutes.pricing}
                sx={{
                  color: '#414651',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  padding: '10px 16px',
                }}
              >
                <ListItemText
                  primary="Pricing"
                  primaryTypographyProps={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '14px',
                    lineHeight: '20px',
                  }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                href={PublicRoutes.login}
                sx={{
                  color: '#414651',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  padding: '10px 16px',
                }}
              >
                <ListItemText
                  primary="Login"
                  primaryTypographyProps={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '14px',
                    lineHeight: '20px',
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </NavWrapper>
  );
};

export default NavSection;
